import { useCallback, useState, useMemo } from "react";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "sonner";
import { PostAnswerActionsConfig } from "@/api/simulator/simulator.model";
import { useDebouncedCallback } from "@/lib/useDebouncedCallback";
import { useSimulatorApi, useGetSimulator } from "@/v2/entities/simulator/Simulator.api";

export function usePostAnswerActionsHandlers(simulatorId?: string) {
    const queryClient = useQueryClient();
    const simulatorApi = useSimulatorApi();

    const { data: simulator } = useGetSimulator(simulatorId);

    const [localOverride, setLocalOverride] = useState<PostAnswerActionsConfig | null>(null);
    const [updateStatus, setUpdateStatus] = useState<"in_progress" | "done">("done");

    const serverConfig = useMemo((): PostAnswerActionsConfig => ({
        status: simulator?.postAnswerActions?.status ?? null,
        tagsToAdd: simulator?.postAnswerActions?.tagsToAdd || [],
        customFields: simulator?.postAnswerActions?.customFields || [],
    }), [simulator?.postAnswerActions]);

    const localConfig = localOverride ?? serverConfig;

    const { mutateAsync: updateSimulator } = useMutation({
        mutationKey: ["updateSimulator", simulatorId],
        mutationFn: (input: Parameters<typeof simulatorApi.updateSimulator>[1]) =>
            simulatorApi.updateSimulator(simulatorId ?? "", input),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["simulator", simulatorId] });
            setUpdateStatus("done");
        },
        onError: () => {
            setUpdateStatus("done");
        },
    });

    const saveConfig = useCallback(
        async (config: PostAnswerActionsConfig) => {
            if (!simulatorId || !simulator) return;

            setUpdateStatus("in_progress");

            try {
                await updateSimulator({
                    name: simulator.name,
                    postAnswerActions: {
                        status: config.status || null,
                        tagsToAdd: config.tagsToAdd || [],
                        customFields: config.customFields || [],
                    },
                });
            } catch (error) {
                toast.error("Failed to save post-answer actions");
            }
        },
        [simulatorId, simulator, updateSimulator]
    );

    const debouncedSave = useDebouncedCallback(saveConfig, 200);

    const handleStatusChange = useCallback(
        (status: string) => {
            const newConfig = {
                ...localConfig,
                status: status || null,
            };
            setLocalOverride(newConfig);
            debouncedSave(newConfig);
        },
        [localConfig, debouncedSave]
    );

    const handleAddTag = useCallback(
        (tag: string): { success: boolean; error?: string } => {
            const normalizedTag = tag.trim().replace(/\s+/g, " ");

            if (!normalizedTag) {
                return { success: false, error: "Tag cannot be empty" };
            }

            if (localConfig.tagsToAdd?.includes(normalizedTag)) {
                return { success: false, error: "Tag already exists" };
            }

            const newConfig = {
                ...localConfig,
                tagsToAdd: [...(localConfig.tagsToAdd || []), normalizedTag],
            };
            setLocalOverride(newConfig);
            debouncedSave(newConfig);

            return { success: true };
        },
        [localConfig, debouncedSave]
    );

    const handleRemoveTag = useCallback(
        (tag: string) => {
            const newConfig = {
                ...localConfig,
                tagsToAdd: localConfig.tagsToAdd?.filter((t) => t !== tag) || [],
            };
            setLocalOverride(newConfig);
            debouncedSave(newConfig);
        },
        [localConfig, debouncedSave]
    );

    const handleAddCustomField = useCallback(() => {
        const newConfig = {
            ...localConfig,
            customFields: [
                ...(localConfig.customFields || []),
                { fieldId: "", value: "" },
            ],
        };
        setLocalOverride(newConfig);
    }, [localConfig]);

    const handleUpdateCustomField = useCallback(
        (index: number, fieldId: string, value: string | string[]) => {
            const newFields = [...(localConfig.customFields || [])];
            newFields[index] = { fieldId, value };

            const newConfig = {
                ...localConfig,
                customFields: newFields,
            };
            setLocalOverride(newConfig);
            debouncedSave(newConfig);
        },
        [localConfig, debouncedSave]
    );

    const handleRemoveCustomField = useCallback(
        (index: number) => {
            const newFields = localConfig.customFields?.filter((_, i) => i !== index) || [];
            const newConfig = {
                ...localConfig,
                customFields: newFields,
            };
            setLocalOverride(newConfig);
            debouncedSave(newConfig);
        },
        [localConfig, debouncedSave]
    );

    return {
        localConfig,
        updateStatus,
        handleStatusChange,
        handleAddTag,
        handleRemoveTag,
        handleAddCustomField,
        handleUpdateCustomField,
        handleRemoveCustomField,
    };
}

